import React, { useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import { CustomModal } from "../../components";
import {
  useDeleteGameMutation,
  useFetchAllGamesQuery,
  useUpdateGameMutation,
  useFetchProfilesQuery,
} from "../../store/api/apiSlice";

const GamePlayManagement = () => {
  const { data: gamesData = { playgame: [] }, isLoading } =
    useFetchAllGamesQuery();

  const [updateGame] = useUpdateGameMutation();
  const [deleteGame] = useDeleteGameMutation();
  const [isModalOpen, setModalOpen] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [selectedInvite, setSelectedInvitee] = useState("");
  const [selectedGame, setSelectedGame] = useState(null);
  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(0);
  const baseUrl = process.env.REACT_APP_SERVER_URL;

  const openModal = (game) => {
    setSelectedGame(game);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedGame(null);
  };

  const openViewModal = async (id, invitee) => {
    const xAuthToken =
      JSON.parse(localStorage.getItem("token") || "{}")?.token || "";
    const headers = {
      "secret-key": process.env.REACT_APP_SECRET_KEY || "",
      "x-auth-token": xAuthToken,
    };

    try {
      const response = await fetch(
        `${baseUrl}/api/play-game/get-single/${id}`,
        {
          headers,
        }
      );
      if (response.ok) {
        const data = await response.json();
        setSelectedGame(data.playgame);
        setSelectedInvitee(invitee);

        setViewModalOpen(true);
      } else {
        toast.error("Failed to fetch game details.");
      }
    } catch (error) {
      toast.error("An error occurred while fetching game details.");
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedGame(null);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const updatedData = {
      invitee_email: selectedGame.invitee_email,
      invited_date: selectedGame.invited_date,
      invitee_response_datetime: selectedGame.invitee_response_datetime,
      is_award_retained: selectedGame.is_award_retained,
      is_charitable: selectedGame.is_charitable,
      invitee_profile_id: selectedGame.invitee_profile_id,
      phone: selectedGame.phone,
      invitee_clicked: selectedGame.invitee_clicked,
      invitee_signup: selectedGame.invitee_signup,
      invitee_paid: selectedGame.invitee_paid,
      is_email_success: selectedGame.is_email_success,
    };
    await updateGame({ id: selectedGame._id, gameData: updatedData });
    closeModal();
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setSelectedGame((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handlePageChange = ({ selected }) => setCurrentPage(selected);

  const offset = currentPage * itemsPerPage;
  const currentItems = gamesData.playgame.slice(offset, offset + itemsPerPage);
  const totalPages = Math.ceil(gamesData.playgame.length / itemsPerPage);
 
  const renderPaginationButtons = () => {
    let buttons = [];
    const maxButtons = 10;
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxButtons - 1);

    if (endPage - startPage < maxButtons - 1) {
      startPage = Math.max(1, endPage - maxButtons + 1);
    }

    if (startPage > 1) {
      buttons.push(
        <Button
          key={1}
          variant={1 === currentPage ? "primary" : "outline-primary"}
          onClick={() => handlePageChange({ selected: 1 })}
          className="me-2"
        >
          1
        </Button>
      );
      if (startPage > 2) {
        buttons.push(<span key="ellipsis-start">...</span>);
      }
    }

    for (let i = startPage; i < endPage; i++) {
      buttons.push(
        <Button
          key={i}
          variant={i === currentPage ? "primary" : "outline-primary"}
          onClick={() => handlePageChange({ selected: i })}
          className="me-2"
        >
          {i}
        </Button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis-end">...</span>);
      }
      buttons.push(
        <Button
          key={totalPages}
          variant={totalPages === currentPage ? "primary" : "outline-primary"}
          onClick={() => handlePageChange({ selected: totalPages })}
          className="me-2"
        >
          {totalPages}
        </Button>
      );
    }

    return buttons;
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return !isNaN(d.getTime()) ? d.toISOString().split("T")[0] : "";
  };

  if (isLoading) return <p>Loading...</p>;

  return (
    <div className="container">
      <h3 className="mb-4 gameplayManagement_title">
        GamePlay Management
      </h3>

      <Table striped bordered hover responsive className="games-table">
        <thead>
          <tr>
            <th>Random ID</th>
            <th>Invitee Name</th>
            <th>Invitee Email</th>
            <th>Invited Date</th>
            <th>Response Date</th>
            <th>Award Retained</th>
            <th>Charitable</th>
            <th>Profile ID</th>
            <th>Phone</th>
            <th>Clicked</th>
            <th>Signup</th>
            <th>Paid</th>
            <th>Email Success</th>
            <th
              className="gameplayManagement_ActionTh"
            >
              Actions
            </th>{" "}
          </tr>
        </thead>
        <tbody>
          {currentItems.map((game) => (
            <React.Fragment key={game._id}>
              {game.invitee_name.length > 0 ? (
                game.invitee_name.map((invitee, index) => (
                  <tr
                    key={`${game._id}-${index}`}
                    onClick={() => openViewModal(game._id, invitee)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{game.randomId}</td>
                    <td>
                      {invitee.invitee_fname} {invitee.invitee_lname}
                    </td>
                    <td>{game.invitee_email}</td>
                    <td>{formatDate(game.invited_date)}</td>
                    <td>{formatDate(game.invitee_response_datetime)}</td>
                    <td>{game.is_award_retained ? "Yes" : "No"}</td>
                    <td>{game.is_charitable ? "Yes" : "No"}</td>
                    <td>{game.invitee_profile_id}</td>
                    <td>{game.phone}</td>
                    <td>{game.invitee_clicked ? "Yes" : "No"}</td>
                    <td>{game.invitee_signup ? "Yes" : "No"}</td>
                    <td>{game.invitee_paid ? "Yes" : "No"}</td>
                    <td>{game.is_email_success ? "Yes" : "No"}</td>
                    <td
                     className="gameplayManagement_td_main"
                    >
                      <div
                       className="admin_Actions_gap"
                      >
                        <img
                          className="Update-delete-icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteGame(game._id);
                          }}
                          width="30"
                          height="30"
                          src="https://img.icons8.com/ios-glyphs/30/filled-trash.png"
                          alt="filled-trash"
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr
                  onClick={() => openViewModal(game._id)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{game.randomId}</td>
                  <td>N/A</td>
                  <td>{game.invitee_email}</td>
                  <td>{formatDate(game.invited_date)}</td>
                  <td>{formatDate(game.invitee_response_datetime)}</td>
                  <td>{game.is_award_retained ? "Yes" : "No"}</td>
                  <td>{game.is_charitable ? "Yes" : "No"}</td>
                  <td>{game.invitee_profile_id}</td>
                  <td>{game.phone}</td>
                  <td>{game.invitee_clicked ? "Yes" : "No"}</td>
                  <td>{game.invitee_signup ? "Yes" : "No"}</td>
                  <td>{game.invitee_paid ? "Yes" : "No"}</td>
                  <td>{game.is_email_success ? "Yes" : "No"}</td>
                  <td
                    className="gameplayManagement_td_main"
                  >
                    <div
                     className="admin_Actions_gap"
                    >
                      <img
                        className="Update-delete-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal(game);
                        }}
                        width="30"
                        height="30"
                        src="https://img.icons8.com/ios-glyphs/30/create-new.png"
                        alt="create-new"
                      />
                      <img
                        className="Update-delete-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteGame(game._id);
                        }}
                        width="30"
                        height="30"
                        src="https://img.icons8.com/ios-glyphs/30/filled-trash.png"
                        alt="filled-trash"
                      />
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>

      <div
        className="pagination-controls gameplayManagement_pagination"
     
      >
        {renderPaginationButtons()}
      </div>

      {isViewModalOpen && (
        <CustomModal isOpen={isViewModalOpen} onClose={closeViewModal}>
          <div className="gameplayManagement_detail_page">
            <h3>Game Details</h3>

            <td>
              <p>
                <strong>Invitee Name:</strong>{" "}
                {selectedInvite
                  ? `${selectedInvite.invitee_fname} ${selectedInvite.invitee_lname}`
                  : "N/A"}
              </p>
            </td>
            <p>
              <strong>Invitee Email:</strong> {selectedGame?.invitee_email}
            </p>
            <p>
              <strong>Invited Date:</strong>{" "}
              {formatDate(selectedGame?.invited_date)}
            </p>
            <p>
              <strong>Response Date:</strong>{" "}
              {formatDate(selectedGame?.invitee_response_datetime)}
            </p>
            <p>
              <strong>Award Retained:</strong>{" "}
              {selectedGame?.is_award_retained ? "Yes" : "No"}
            </p>
            <p>
              <strong>Charitable:</strong>{" "}
              {selectedGame?.is_charitable ? "Yes" : "No"}
            </p>
            <p>
              <strong>Profile ID:</strong> {selectedGame?.invitee_profile_id}
            </p>
            <p>
              <strong>Phone:</strong> {selectedGame?.phone}
            </p>
            <p>
              <strong>Clicked:</strong>{" "}
              {selectedGame?.invitee_clicked ? "Yes" : "No"}
            </p>
            <p>
              <strong>Signup:</strong>{" "}
              {selectedGame?.invitee_signup ? "Yes" : "No"}
            </p>
            <p>
              <strong>Paid:</strong> {selectedGame?.invitee_paid ? "Yes" : "No"}
            </p>
            <p>
              <strong>Email Success:</strong>{" "}
              {selectedGame?.is_email_success ? "Yes" : "No"}
            </p>
            <p>
              <strong>Invitation Url:</strong>{" "}
              <a
                href={`${baseUrl}/invitation/${selectedGame?.randomId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="gameplayManagement_invitedURL"
              >
                {`${baseUrl}/invitation/${selectedGame?.randomId}`}
              </a>
            </p>
            <Button
              onClick={closeViewModal}
              variant="outline-secondary"
           className="gameplayManagement_closeBtn"
            >
              Close
            </Button>
          </div>
        </CustomModal>
      )}

      <Modal show={isModalOpen} onHide={closeModal} centered>
        <Modal.Header>
          <Modal.Title>Edit Game Play Details</Modal.Title>
          <button className="btn-close" onClick={closeModal} />
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleUpdate}>
            <Form.Group controlId="inviteeEmail">
              <Form.Label>Invitee Email</Form.Label>
              <Form.Control
                type="email"
                value={selectedGame?.invitee_email || ""}
                readOnly
              />
            </Form.Group>
            <Form.Group controlId="invitedDate" className="mt-3">
              <Form.Label>Invited Date</Form.Label>
              <Form.Control
                type="date"
                name="invited_date"
                value={formatDate(selectedGame?.invited_date)}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group controlId="responseDate" className="mt-3">
              <Form.Label>Response Date</Form.Label>
              <Form.Control
                type="date"
                name="invitee_response_datetime"
                value={formatDate(selectedGame?.invitee_response_datetime)}
                onChange={handleInputChange}
              />
            </Form.Group>

            <div className="d-flex flex-wrap gap-3 mt-3">
              <Form.Check
                inline
                type="checkbox"
                label="Award Retained"
                name="is_award_retained"
                checked={selectedGame?.is_award_retained || false}
                onChange={handleInputChange}
              />
              <Form.Check
                inline
                type="checkbox"
                label="Charitable"
                name="is_charitable"
                checked={selectedGame?.is_charitable || false}
                onChange={handleInputChange}
              />
              <Form.Check
                inline
                type="checkbox"
                label="Clicked"
                name="invitee_clicked"
                checked={selectedGame?.invitee_clicked || false}
                onChange={handleInputChange}
              />
              <Form.Check
                inline
                type="checkbox"
                label="Signup"
                name="invitee_signup"
                checked={selectedGame?.invitee_signup || false}
                onChange={handleInputChange}
              />
              <Form.Check
                inline
                type="checkbox"
                label="Paid"
                name="invitee_paid"
                checked={selectedGame?.invitee_paid || false}
                onChange={handleInputChange}
              />
              <Form.Check
                inline
                type="checkbox"
                label="Email Success"
                name="is_email_success"
                checked={selectedGame?.is_email_success || false}
                onChange={handleInputChange}
              />
            </div>

            <Form.Group controlId="profileId" className="mt-3">
              <Form.Label>Profile ID</Form.Label>
              <Form.Control
                type="text"
                value={selectedGame?.invitee_profile_id || ""}
                readOnly
              />
            </Form.Group>
            <Form.Group controlId="phone" className="mt-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="text"
                name="phone"
                value={selectedGame?.phone || ""}
                onChange={handleInputChange}
              />
            </Form.Group>

            <div className="d-flex justify-content-end mt-4">
              <Button variant="secondary" onClick={closeModal} className="me-2">
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Save
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default GamePlayManagement;
